import { create } from 'zustand';
import {
  GangCreationFormData,
  GangCreationStep,
} from '../types/gangCreationTypes';

export type GangLifecycleMode = 'create' | 'edit';

export interface CachedUser {
  id: string;
  fullName: string;
  email: string;
  roles?: string[];
}

interface GangLifecycleUIState {
  isOpen: boolean;
  mode: GangLifecycleMode;
  gangId: string | null;
  activeStep: GangCreationStep;
  completedSteps: Set<GangCreationStep>;
  initialData: GangCreationFormData | null;
  userCache: Map<string, CachedUser>;
  isSubmitting: boolean;
}

interface GangLifecycleUIActions {
  openCreateDrawer: () => void;
  openEditDrawer: (
    params: {
      gangId: string;
      initialData: GangCreationFormData;
      completedSteps?: GangCreationStep[];
      activeStep?: GangCreationStep;
    } | null
  ) => void;
  closeDrawer: () => void;
  setMode: (mode: GangLifecycleMode) => void;
  setGangId: (gangId: string | null) => void;
  setActiveStep: (step: GangCreationStep) => void;
  markStepCompleted: (step: GangCreationStep) => void;
  unmarkStepCompleted: (step: GangCreationStep) => void;
  isStepCompleted: (step: GangCreationStep) => boolean;
  setCompletedSteps: (steps: GangCreationStep[]) => void;
  resetCompletedSteps: () => void;
  setInitialData: (data: GangCreationFormData | null) => void;
  cacheUser: (user: CachedUser) => void;
  clearUserCache: () => void;
  setIsSubmitting: (isSubmitting: boolean) => void;
  resetAllState: () => void;
}

const createEmptyCompletedSet = () => new Set<GangCreationStep>();
const createEmptyUserCache = () => new Map<string, CachedUser>();
const createInitialState = (): GangLifecycleUIState => ({
  isOpen: false,
  mode: 'create',
  gangId: null,
  activeStep: GangCreationStep.NAME,
  completedSteps: createEmptyCompletedSet(),
  initialData: null,
  userCache: createEmptyUserCache(),
  isSubmitting: false,
});

export type GangLifecycleStore = GangLifecycleUIState & GangLifecycleUIActions;

export const useGangLifecycleStore = create<GangLifecycleStore>()(
  (set, get) => ({
    ...createInitialState(),

    openCreateDrawer: () =>
      set(() => ({
        ...createInitialState(),
        isOpen: true,
        mode: 'create',
      })),

    openEditDrawer: params =>
      set(state => {
        const nextState = { ...state };
        if (!params) {
          nextState.isOpen = true;
          nextState.mode = 'edit';
          nextState.activeStep = GangCreationStep.NAME;
          nextState.completedSteps = createEmptyCompletedSet();
          return nextState;
        }

        const { gangId, initialData, completedSteps, activeStep } = params;
        nextState.isOpen = true;
        nextState.mode = 'edit';
        nextState.gangId = gangId;
        nextState.initialData = initialData;
        nextState.activeStep = activeStep ?? GangCreationStep.NAME;
        nextState.completedSteps = completedSteps
          ? new Set(completedSteps)
          : createEmptyCompletedSet();
        nextState.userCache = createEmptyUserCache();
        return nextState;
      }),

    closeDrawer: () =>
      set(state => ({
        ...state,
        isOpen: false,
      })),

    setMode: mode =>
      set(state => ({
        ...state,
        mode,
      })),

    setGangId: gangId =>
      set(state => ({
        ...state,
        gangId,
      })),

    setActiveStep: step =>
      set(state => ({
        ...state,
        activeStep: step,
      })),

    markStepCompleted: step =>
      set(state => {
        const next = new Set(state.completedSteps);
        next.add(step);
        return {
          ...state,
          completedSteps: next,
        };
      }),

    unmarkStepCompleted: step =>
      set(state => {
        const next = new Set(state.completedSteps);
        next.delete(step);
        return {
          ...state,
          completedSteps: next,
        };
      }),

    isStepCompleted: step => get().completedSteps.has(step),

    setCompletedSteps: steps =>
      set(state => ({
        ...state,
        completedSteps: new Set(steps),
      })),

    resetCompletedSteps: () =>
      set(state => ({
        ...state,
        completedSteps: createEmptyCompletedSet(),
      })),

    setInitialData: data =>
      set(state => ({
        ...state,
        initialData: data,
      })),

    cacheUser: user =>
      set(state => {
        const nextCache = new Map(state.userCache);
        nextCache.set(user.id, { ...user });
        return {
          ...state,
          userCache: nextCache,
        };
      }),

    clearUserCache: () =>
      set(state => ({
        ...state,
        userCache: createEmptyUserCache(),
      })),

    setIsSubmitting: isSubmitting =>
      set(state => ({
        ...state,
        isSubmitting,
      })),

    resetAllState: () => set(() => createInitialState()),
  })
);
